import { type MouseEvent, useCallback, useMemo } from 'react';

import { Box, Button, Typography, useTheme } from '@mui/material';
import { getCurrencyByCountryCode } from '@realadvisor/constants';
import { Image } from '@realadvisor/image';
import { formatDistance } from 'date-fns';
import { House } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { GetDashboardCmaReportsQuery } from '@graphql/__generated__/graphql';
import {
  formatAddress,
  formatCmaReportPrice,
} from '@modules/_shared/utils/formatting';
import {
  makeRowKeyDownHandler,
  navigateWithModifiers,
} from '@modules/_shared/utils/navigation';
import { useLocale } from '@shared/hooks/locale';
import { getStaticMapLink } from '@shared/utils/static-map';

type DashboardCMACardItemProps = {
  cmaReport: GetDashboardCmaReportsQuery['cma_reports'][number];
};

export const DashboardCMACardItem = ({
  cmaReport,
}: DashboardCMACardItemProps) => {
  const { t, locale, dateLocale } = useLocale();
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const navigate = useNavigate();

  const contactName = cmaReport.contact
    ? `${cmaReport.contact.first_name ?? ''} ${
        cmaReport.contact.last_name ?? ''
      }`.trim()
    : null;

  const propertyAddress = cmaReport.property
    ? formatAddress(cmaReport.property, ', ')
    : '';

  const imageUrl = cmaReport.cover_image?.url ?? null;

  const lat = cmaReport.property?.lat;
  const lng = cmaReport.property?.lng;
  const satelliteMapUrl = useMemo(() => {
    if (lat == null || lng == null) {
      return null;
    }
    return getStaticMapLink({
      lat,
      lng,
      zoom: 18,
      style: 'satellite',
      width: 128,
      height: 128,
      markers: [],
    });
  }, [lat, lng]);

  const currency = getCurrencyByCountryCode(
    cmaReport.property?.country_code ?? 'CH',
  );
  const formattedPrice = formatCmaReportPrice(cmaReport, currency, locale);

  const timeAgo = formatDistance(new Date(cmaReport.created_at), new Date(), {
    locale: dateLocale,
    addSuffix: true,
  });

  const cmaPath = `/cma-reports/${cmaReport.id}/page-cover`;
  const handleOpenClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      navigateWithModifiers(event, navigate, cmaPath);
    },
    [navigate, cmaPath],
  );
  const handleButtonClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    handleOpenClick(e);
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={handleOpenClick}
      onKeyDown={makeRowKeyDownHandler(cmaPath, navigate)}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        alignItems: 'center',
        pr: 2,
        minHeight: 64,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          bgcolor: 'action.hover',
          borderColor: 'grey.300',
        },
      }}
    >
      <Box sx={{ pr: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderTopLeftRadius: 2,
            borderBottomLeftRadius: 2,
            backgroundColor: palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {imageUrl ? (
            <Image
              src={imageUrl}
              css={{
                width: 64,
                height: 64,
                objectFit: 'cover',
              }}
              options={{ w: 256, h: 256, f: 'jpg' }}
            />
          ) : satelliteMapUrl ? (
            <Box
              component="img"
              src={satelliteMapUrl}
              alt=""
              sx={{
                width: 64,
                height: 64,
                display: 'block',
                objectFit: 'cover',
              }}
            />
          ) : (
            <House size={24} strokeWidth={1.5} color={palette.grey[400]} />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          mr: 1,
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: 600,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {propertyAddress || t('No address')}
        </Typography>

        <Typography
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {contactName && `${contactName} • `}
          {formattedPrice}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {timeAgo}
        </Typography>

        <Button
          size="small"
          variant="soft"
          color="primary"
          aria-label={t('Open CMA report')}
          onClick={handleButtonClick}
          onAuxClick={handleButtonClick}
        >
          {t('Open')}
        </Button>
      </Box>
    </Box>
  );
};
